import SenderDetails from "./sender-details";
import RecipientDetails from "./recipient-details";
import PackageDetails from "./package-details";
import Addons from "./add-ons";
import Review from "./review";
import { validateEmail } from "@/lib/validation";
import { validateMobileNo, validatePostalCode } from "@/lib/validation";
import axios from "axios";
import { useState } from "react";
import { useRouter } from "next/router";
import { useAppContext } from "../../context/App-context";
import statesJSON from "@/data/states.json";
import TagManager from "react-gtm-module";
import classNames from "classnames";
import useNoInitialRenderEffect from "../../hooks/useNoInitialRenderEffect";
import {
  MIN_ITEM_DESCRIPTION_LENGTH,
  MAX_ITEM_DESCRIPTION_LENGTH,
} from "@/lib/constants";
import { textHasDisallowedCharacters } from "@/utils/languages";

const COUNTRIES_WITH_STATES = Object.keys(statesJSON);

export const getShippingCost = async (weight, country) => {
  if (country && weight) {
    const data = {
      countryCode: country,
      weight,
    };
    try {
      const results = await axios.post("/api/get-shipping-quote", data);
      return results.data.results;
    } catch (err) {
      return err;
    }
  }
};

export const addToCart = async (formData, router) => {
  try {
    const res = await axios.post("/api/submit-shipment-form", {
      data: formData,
      action: "addToCart",
    });
    if (res.data.results === "ok") {
      // GTM
      if (
        process.env.NEXT_PUBLIC_ENV === "production" &&
        !process.env.NEXT_PUBLIC_CI
      ) {
        const allItemCategories = new Set();
        formData.packages.forEach((pkg) => {
          pkg.items.forEach((item) => {
            allItemCategories.add(item.category);
          });
        });
        TagManager.dataLayer({
          dataLayer: {
            event: "addToCart",
            ecommerce: {
              add: {
                actionField: {
                  value: formData.totalAmount,
                  country: formData.recipientCountry,
                  purpose: formData.purpose,
                  category: [...allItemCategories],
                },
              },
            },
          },
        });
      }

      try {
        axios.delete("/api/unsubmitted-forms");
      } catch (err) {
        console.log(err);
      }
      router.push("/dashboard/cart");
    }
  } catch (err) {
    console.log(err);
  }
};

export default function FormContainer({
  step,
  setStep,
  formData,
  setFormData,
  isEdit,
  handleFinishEdit,
  userBoxes,
  savedRecipients,
}) {
  const router = useRouter();
  const { setOrdersToCheckout } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const parseNewLine = (str) => {
    const returnStr = [];
    for (const string of str.split("\n")) {
      returnStr.push(
        <p key={string} className="">
          {string}
        </p>
      );
    }
    return returnStr;
  };
  const handleCheckout = async () => {
    try {
      setOrdersToCheckout([formData]);
      const query = {
        from: "create-shipment-form",
      };
      router.push({ pathname: "/checkout", query }, "/checkout");
    } catch (err) {
      console.log(err);
    }
  };

  const updateFormData = (key, value) => {
    setFormData((formData) => {
      return { ...formData, [key]: value };
    });
  };

  const validateFloatValue = (float) => {
    if (float === undefined) return false;
    if (typeof float !== "string") float = float.toString();
    const numOfDots = float.match(/[.]/g);
    return (
      !!parseFloat(float) && (numOfDots === null ? true : numOfDots.length <= 1)
    );
  };

  //! validationStates
  const [validSenderFirstName, setValidSenderFirstName] = useState("none");
  const [validSenderEmail, setValidSenderEmail] = useState("none");
  const [validSenderMobileNo, setValidSenderMobileNo] = useState("none");
  const [validPurpose, setValidPurpose] = useState(
    formData.purpose ? true : "none"
  );

  const senderValidationStates = [
    {
      state: validSenderFirstName,
      setState: setValidSenderFirstName,
      id: "senderFirstName",
    },
    {
      state: validSenderEmail,
      setState: setValidSenderEmail,
      id: "senderEmail",
    },
    {
      state: validSenderMobileNo,
      setState: setValidSenderMobileNo,
      id: "senderMobileNo",
    },
    {
      state: validPurpose,
      setState: setValidPurpose,
      id: "purpose",
    },
  ];

  const [validRecipientName, setValidRecipientName] = useState("none");
  const [validRecipientMobileNo, setValidRecipientMobileNo] = useState("none");
  const [validRecipientAddress, setValidRecipientAddress] = useState(
    formData.recipientAddress ? true : "none"
  );
  const [validRecipientPostalCode, setValidRecipientPostalCode] =
    useState("none");
  const [validRecipientCity, setValidRecipientCity] = useState(
    formData.recipientCity ? true : "none"
  );
  const [validRecipientState, setValidRecipientState] = useState(
    formData.recipientState ? true : "none"
  );
  const [validRecipientCountry, setValidRecipientCountry] = useState(
    formData.recipientCountry ? true : "none"
  );

  const recipientValidationStates = [
    {
      state: validRecipientName,
      setState: setValidRecipientName,
      id: "recipientName",
    },
    {
      state: validRecipientMobileNo,
      setState: setValidRecipientMobileNo,
      id: "recipientMobileNo",
    },
    {
      state: validRecipientAddress,
      setState: setValidRecipientAddress,
      id: "recipientAddress",
    },
    {
      state: validRecipientPostalCode,
      setState: setValidRecipientPostalCode,
      id: "recipientPostalCode",
    },
    {
      state: validRecipientCity,
      setState: setValidRecipientCity,
      id: "recipientCity",
    },
    {
      state: validRecipientState,
      setState: setValidRecipientState,
      id: "recipientState",
    },
    {
      state: validRecipientCountry,
      setState: setValidRecipientCountry,
      id: "recipientCountry",
    },
  ];

  const focusOnInvalidInput = () => {
    setTimeout(() => {
      const input = document.querySelector(".border-error-red");
      const errorText = document.querySelector(".text-error-red");
      let inputDistFromTop = 0;
      let errorDistFromTop = 0;
      if (input) {
        inputDistFromTop =
          window.pageYOffset + input.getBoundingClientRect().top;
      }
      if (errorText) {
        errorDistFromTop =
          window.pageYOffset + errorText.getBoundingClientRect().top;
      }
      if (
        errorText &&
        (errorDistFromTop < inputDistFromTop || inputDistFromTop === 0)
      ) {
        errorText.style.scrollMarginTop = "2.5rem";
        errorText.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else if (input) {
        input.style.scrollMarginTop = "2.5rem";
        input.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        input.focus({ preventScroll: true });
      }
    }, 100);
  };

  const isFormFilled = (step) => {
    const invalidFields = [];
    switch (step) {
      case 0:
        //sender details check
        if (!formData.senderFirstName) {
          invalidFields.push("First Name");
          setValidSenderFirstName(false);
        }
        if (!validateEmail(formData.senderEmail)) {
          invalidFields.push("Email");
          setValidSenderEmail(false);
        }
        if (
          validateMobileNo(formData.senderMobileNo, formData.senderCallCode)
        ) {
          invalidFields.push("Mobile No");
          setValidSenderMobileNo(false);
        }
        if (!formData.purpose) {
          invalidFields.push("Purpose");
          setValidPurpose(false);
        }

        if (invalidFields.length > 0) {
          focusOnInvalidInput();
          return false;
        }
        return true;

      //recipient details check
      case 1:
        if (
          !formData.recipientName ||
          textHasDisallowedCharacters(formData.recipientName)
        ) {
          invalidFields.push("recipientName");
          setValidRecipientName(false);
        }
        if (
          validateMobileNo(
            formData.recipientMobileNo,
            formData.recipientCallCode
          )
        ) {
          invalidFields.push("Mobile No");
          setValidRecipientMobileNo(false);
        }
        if (
          !formData.recipientAddress ||
          textHasDisallowedCharacters(formData.recipientAddress)
        ) {
          invalidFields.push("Address");
          setValidRecipientAddress(false);
        }
        if (
          validatePostalCode(
            formData.recipientPostalCode,
            formData.recipientCountry
          )
        ) {
          invalidFields.push("Postal Code");
          setValidRecipientPostalCode(false);
        }
        if (!formData.recipientCity) {
          invalidFields.push("City");
          setValidRecipientCity(false);
        }
        if (COUNTRIES_WITH_STATES.includes(formData.recipientCountry)) {
          if (!formData.recipientState) {
            invalidFields.push("State");
            setValidRecipientState(false);
          }
        }

        if (formData.recipientCountry === "") {
          invalidFields.push("Country");
          setValidRecipientCountry(false);
        }

        if (invalidFields.length > 0) {
          focusOnInvalidInput();
          return false;
        }

        return true;

      //package details check
      case 2:
        let dataHolder = { ...formData };
        for (const [packageIndex] of formData.packages.entries()) {
          const parcel = dataHolder.packages[packageIndex];
          parcel.validLength = validateFloatValue(parcel.length);
          parcel.validWidth = validateFloatValue(parcel.width);
          parcel.validHeight = validateFloatValue(parcel.height);
          parcel.validWeight = validateFloatValue(parcel.weight);
          parcel.validPackingType = !!parcel.packingType;
          for (const [itemIndex, item] of parcel.items.entries()) {
            parcel.items[itemIndex].validDescription =
              !!item.description &&
              item.description?.trim().length >= MIN_ITEM_DESCRIPTION_LENGTH &&
              item.description?.trim().length <= MAX_ITEM_DESCRIPTION_LENGTH;
            parcel.items[itemIndex].validQuantity = !!item.quantity;
            parcel.items[itemIndex].validCategory = !!item.category;
            parcel.items[itemIndex].validTotalWeight = validateFloatValue(
              item.totalWeight
            );
            parcel.items[itemIndex].validTotalValue = validateFloatValue(
              item.totalValue
            );
          }
        }
        setFormData(dataHolder);

        if (
          !dataHolder.packages.every(
            (parcel) =>
              parcel.validLength &&
              parcel.validWidth &&
              parcel.validHeight &&
              parcel.validWeight &&
              parcel.packingType &&
              parcel.items.every(
                (item) =>
                  item.validDescription &&
                  item.validQuantity &&
                  item.validCategory &&
                  item.validTotalWeight &&
                  item.validTotalValue
              )
          )
        ) {
          focusOnInvalidInput();
          return false;
        }
        return true;

      //shipment details check
      case 3:
        return (
          formData.shippingMethod &&
          typeof formData.isInsured === "boolean" &&
          typeof formData.directSignature === "boolean" &&
          (formData.dutiesPaidBy === "RECIPIENT" ||
            formData.dutiesPaidBy === "SENDER")
        );
      default:
        return false;
    }
  };

  const trackShipmentForm = async (step, formData) => {
    const data = {
      step,
      formData,
    };
    await axios.post("/api/track-shipment-form", data);
  };

  useNoInitialRenderEffect(() => {
    if (step !== 4) setIsError(false);
  }, [step]);

  const renderCurrentStep = (step) => {
    switch (step) {
      case 0:
        return (
          <SenderDetails
            formData={formData}
            setFormData={setFormData}
            senderValidationStates={senderValidationStates}
            parseNewLine={parseNewLine}
            updateFormData={updateFormData}
          />
        );
      case 1:
        return (
          <RecipientDetails
            formData={formData}
            setFormData={setFormData}
            isRural={formData.isRural}
            recipientValidationStates={recipientValidationStates}
            COUNTRIES_WITH_STATES={COUNTRIES_WITH_STATES}
            updateFormData={updateFormData}
            savedRecipients={savedRecipients}
          />
        );
      case 2:
        return (
          <PackageDetails
            formData={formData}
            setFormData={setFormData}
            userBoxes={userBoxes}
            isError={isError}
            setIsError={setIsError}
            setIsLoading={setIsLoading}
          />
        );
      case 3:
        return (
          <Addons
            formData={formData}
            setFormData={setFormData}
            setIsLoading={setIsLoading}
          />
        );
      case 4:
        return (
          <Review
            formData={formData}
            isRural={formData.isRural}
            setFormData={setFormData}
            setIsError={setIsError}
          />
        );
    }
  };

  const isNextButtonDisabled = isLoading || isError;

  return (
    <>
      <div className="md:w-[80%] mx-auto flex justify-center">
        <div
          className="flex w-full max-w-3xl justify-center text-gray-4
            text-sm"
        >
          {renderCurrentStep(step)}
        </div>
      </div>
      <div className="flex mx-auto pt-8 pb-16 sm:py-8 justify-center gap-4 md:gap-10">
        {step > 0 ? (
          <button
            type="button"
            id="back-button"
            disabled={isLoading}
            className={classNames(
              "pill-button button-hover border-light-purple text-light-purple w-[160px] md:w-[180px] h-[40px]",
              "disabled:cursor-not-allowed disabled:bg-gray-2 disabled:text-light-purple",
              "shadow"
            )}
            onClick={() => {
              setStep(step - 1);
              window.scroll(0, 0);
            }}
          >
            Back
          </button>
        ) : null}

        {step < 4 ? (
          <button
            type="button"
            id="next-button"
            disabled={isNextButtonDisabled}
            className={classNames(
              "pill-button button-hover bg-light-purple text-white w-[160px] md:w-[180px] h-[40px]",
              "disabled:bg-disabled-purple disabled:cursor-not-allowed disabled:border-transparent",
              "shadow"
            )}
            onClick={() => {
              if (isFormFilled(step)) {
                setStep(step + 1);
                window.scroll(0, 0);

                trackShipmentForm(step, formData);
              }
            }}
          >
            Next
          </button>
        ) : !isEdit ? (
          <div>
            <button
              type="button"
              disabled={isNextButtonDisabled}
              id="checkout-button"
              className={classNames(
                "pill-button button-hover bg-light-purple text-white w-[160px] md:w-[180px] h-[40px]",
                "disabled:bg-disabled-purple disabled:cursor-not-allowed disabled:border-transparent",
                "shadow"
              )}
              onClick={() => handleCheckout()}
            >
              Choose Dates
            </button>
            <div className="text-center">
              <p
                id="add-to-cart"
                disabled={isLoading || isError}
                className={classNames(
                  "mt-4 underline text-center text-sm font-medium select-none",
                  isNextButtonDisabled
                    ? "cursor-not-allowed text-border-dark-gray"
                    : "cursor-pointer text-light-purple"
                )}
                onClick={() => {
                  if (!isNextButtonDisabled) addToCart(formData, router);
                }}
              >
                Add to Cart
              </p>
            </div>
          </div>
        ) : (
          <button
            type="button"
            id="finish-edit-button"
            disabled={isNextButtonDisabled}
            className={classNames(
              "pill-button button-hover bg-light-purple text-white w-[160px] md:w-[180px] h-[40px]",
              "disabled:bg-disabled-purple disabled:cursor-not-allowed disabled:border-transparent",
              "shadow"
            )}
            onClick={() => handleFinishEdit()}
          >
            Finish Edit
          </button>
        )}
      </div>
    </>
  );
}
